import { Box, IconButton, Typography } from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { isBrowser, isIOS, isTablet } from "react-device-detect";
import { getAPIAsync } from "../../api/apiCalls";
import Provider, { cancelGetRequest } from "../../api/provider";
import { detectURL, extractImgTags, HtmlEncode, icon, restrictMaxLength, returnFormatedStringForChat, WEBSITE_PATH, windowPostMessage } from "../../common/common";
import { replaceAllData } from "../../common/commonJS";
import { resource } from "../../common/resource";
import CreateMenu from "../../components/menu";
import { MessageData, URLDetails } from "../../models/messaging";
import { MenuItems, sharePostMenuItem } from "../menuItems/chatMenuItems";
import ChatEmoji from "./chatEmojis";
import ChatReply from "./chatReply";
import SharePost from "./sharePost";

interface ChatFooterProps {
  senderID: string;
  ucnum: string;
  loader: boolean;
  disabled: boolean;
  reactionEmojiMessageID: string;
  shareDialogRef: any;
  selectedReply: MessageData | null;
  onSendClick: any;
  onThumbsUpClick: any;
  onBookmarkedPostClick: any;
  onMyPostClick: any;
  onReactionEmojiClick: any;
  clearReactionEmojiMessageID: any;
  onReplyCloseClick: any;
  onMessageBoxChanged: any;
}
let restore: number = -1,
  childNode: any = null,
  typingInit: any = null;

const ChatFooter = ({ senderID, ucnum, loader, disabled, reactionEmojiMessageID, shareDialogRef, selectedReply, onSendClick, onThumbsUpClick, onBookmarkedPostClick, onMyPostClick, onReactionEmojiClick, clearReactionEmojiMessageID, onReplyCloseClick, onMessageBoxChanged }: ChatFooterProps, ref: any) => {
  const [isMessageBoxEmpty, setIsMessageBoxEmpty] = useState<boolean>(true);
  const [isEmojiOpen, setIsEmojiOpen] = useState<boolean>(false);
  const [anchorSharePost, setAnchorSharePost] = useState<null | HTMLElement>(null);
  const [linkDetails, setLinkDetails] = useState<URLDetails | null>(null);
  const [rejectedLink, setRejectedLink] = useState<string>("");

  const smRef: any = useRef<any>();
  const spRef: any = useRef<any>();
  const delayDebounceFn: any = useRef<any>(null);
  const isChatFocused: any = useRef<boolean>(false);

  const onURLDetailsSuccess = (response: any) => {
    if (response) {
      const msgBox: any = document.getElementById("messageBox");
      response.data.messageContent = msgBox.innerHTML;
      setLinkDetails(response.data);
    } else {
      setLinkDetails(null);
    }
  };
  const getURLDetails = (url: string) => {
    cancelGetRequest();
    getAPIAsync(Provider.URLS.BASE_URL, Provider.API_URLS.URLDetails, { url: encodeURIComponent(url) }, onURLDetailsSuccess, () => setLinkDetails(null));
  };
  const onCloseClick = () => {
    if (linkDetails) {
      setRejectedLink(linkDetails.url);
      setLinkDetails(null);
    }
  };

  useImperativeHandle(ref, () => ({
    emptyTextBox: () => {
      smRef.current.textContent = "";
      if (window.getSelection()?.empty) {
        window.getSelection()?.empty();
      } else if (window.getSelection()?.removeAllRanges) {
        window.getSelection()?.removeAllRanges();
      }
      setIsMessageBoxEmpty(true);
    },
    isChatFocused: () => {
      return isChatFocused.current;
    },
    showPlaceholder: () => {
      setIsMessageBoxEmpty(true);
    },
    hidePlaceholder: () => {
      setIsMessageBoxEmpty(false);
    },
    getLinkDetails: () => {
      return linkDetails;
    },
    setCaret: () => {
      setCaret();
    },
    saveCaretPosition: () => {
      saveCaretPosition();
    },
    openEmoji: () => {
      setIsEmojiOpen(true);
    },
    closeEmoji: () => {
      setIsEmojiOpen(false);
    },
    closeShare: () => {
      shareDialogRef.current && shareDialogRef.current.closeShareDialog();
    },
    closeShareMenu: () => {
      setAnchorSharePost(null);
    },
    focus: () => {
      smRef.current.focus();
    },
  }));

  const scrollSelectionIntoView = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }
    const firstRange = selection.getRangeAt(0);
    if (firstRange.commonAncestorContainer === document) {
      return;
    }
    const tempAnchorEl = document.createElement("br");
    firstRange.insertNode(tempAnchorEl);
    tempAnchorEl.scrollIntoView({
      block: "end",
    });
    tempAnchorEl.remove();
  };
  const saveCaretPosition = () => {
    let sel = window.getSelection(),
      range = null;
    if (sel && sel.rangeCount) {
      range = sel.getRangeAt(0);
      childNode = sel.anchorNode;
      restore = range.endOffset;
    }
  };
  const setCaret = () => {
    const range = document.createRange(),
      sel = window.getSelection();
    if (childNode) {
      range.setStart(childNode, restore);
    }
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
  };
  const setMessageBoxStatus = (e: any) => {
    const msgBox: any = document.getElementById("messageBox");
    if (msgBox) {
      const trimmedHtml: any = returnFormatedStringForChat(msgBox.innerHTML);
      if ((trimmedHtml !== "" && trimmedHtml !== `<div></div>`) || trimmedHtml === `<div><br></div>` || trimmedHtml === `<br>` || (trimmedHtml === "<br><div></div>" && (e.keyCode === 8 || e.keyCode === 46))) {
        setIsMessageBoxEmpty(false);
        onMessageBoxChanged(false);
        clearTimeout(typingInit);
        typingInit = setTimeout(() => {
          onMessageBoxChanged(true);
        }, 2000);
        if (delayDebounceFn.current !== null) {
          clearTimeout(delayDebounceFn.current);
          delayDebounceFn.current = null;
        }
        const urlsInMessage: Array<string> = detectURL(msgBox.innerHTML);
        if (urlsInMessage.length > 0) {
          const foundLink = urlsInMessage.find((el) => el === rejectedLink);
          if (!foundLink) {
            rejectedLink && setRejectedLink("");
            delayDebounceFn.current = setTimeout(() => {
              getURLDetails(urlsInMessage[0]);
            }, 200);
          }
        } else {
          setLinkDetails(null);
        }
      } else {
        smRef.current.textContent = "";
        setIsMessageBoxEmpty(true);
        onMessageBoxChanged(true);
        setLinkDetails(null);
      }
    }
  };
  const onFocus = () => {
    isChatFocused.current = true;
  };
  const onBlur = () => {
    isChatFocused.current = false;
  };
  const onInput = (e: any) => {
    setMessageBoxStatus(e);
    if (e.keyCode !== 13) {
      saveCaretPosition();
    }
  };
  const onKeyDown = (e: any) => {
    const isWithin: boolean = restrictMaxLength(e, true);
    if (!isWithin) {
      e.preventDefault();
    }
    const msgBox: any = document.getElementById("messageBox");
    if (msgBox) {
      if (e.keyCode === 13 && e.ctrlKey) {
        e.preventDefault();
        saveCaretPosition();
        scrollSelectionIntoView();
        document.execCommand("insertText", false, "\n");
      } else if (e.keyCode === 13 && !e.ctrlKey) {
        e.preventDefault();
        if (window.getSelection()?.empty) {
          window.getSelection()?.empty();
        } else if (window.getSelection()?.removeAllRanges) {
          window.getSelection()?.removeAllRanges();
        }
        const trimmedHtml: any = returnFormatedStringForChat(msgBox.innerHTML);
        if (trimmedHtml !== "") {
          onSendClick();
        }
        childNode = msgBox;
        restore = 0;
        setCaret();
      }
    }
  };
  const onPaste = (e: any) => {
    let pasteContent = e.clipboardData.getData("text");
    const allImages: any = extractImgTags(pasteContent);
    allImages.length > 0 &&
      allImages.forEach((el: any, i: number) => {
        pasteContent = pasteContent.replace(el.outerHTML, `imgTag|${i}`);
      });
    let encodedContent: any = HtmlEncode(pasteContent);
    allImages.length > 0 &&
      allImages.forEach((el: any, i: number) => {
        encodedContent = encodedContent.replace(`imgTag|${i}`, el.outerHTML);
      });

    e.preventDefault();
    document.execCommand("insertHTML", false, encodedContent);
    setMessageBoxStatus(e);
  };
  const onClick = () => {
    saveCaretPosition();
  };
  const onSharePostIconClick = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorSharePost(event.currentTarget);
  const onSharePostClick = () => {
    shareDialogRef.current && shareDialogRef.current.openShareDialog();
    !isBrowser && windowPostMessage({ type: "pushState", pushPage: "share" }, WEBSITE_PATH);
  };

  const handleEmojiAwayClick = () => {
    isEmojiOpen && setIsEmojiOpen(false);
  };
  const onEmojiClick = () => {
    setIsEmojiOpen(!isEmojiOpen);
  };
  const onEmojiCallback = (event: any) => {
    if (reactionEmojiMessageID) {
      onReactionEmojiClick(event);
    } else {
      if (!restrictMaxLength(event, false)) {
        windowPostMessage({ type: "maxCharacterSnack", snackMsg: resource.messageIsTooLong }, WEBSITE_PATH);
        return;
      }
      const msgBox = document.getElementById("messageBox");
      if (restore === -1 && childNode === null) {
        childNode = msgBox;
        restore = 0;
      }
      setCaret();
      setIsMessageBoxEmpty(false);
      const backgroundImage: string = event.currentTarget.querySelector(".emoji-mart-emoji span").style.backgroundImage;
      const backgroundPosition: string = event.currentTarget.querySelector(".emoji-mart-emoji span").style.backgroundPosition;
      document.execCommand("insertHTML", false, `<img ${isIOS ? `srcSet="" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/wcAAgMBAhS+BpQAAAAASUVORK5CYII="` : `src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="`} aria-label="${event.currentTarget.getAttribute("aria-label")}" width="20" height="20" style="${isIOS ? `object-fit: none;` : ``} background-size: 6100% 6100%; background-image:${replaceAllData(backgroundImage, `"`, "")}; background-position: ${backgroundPosition};" />`);
      saveCaretPosition();
      scrollSelectionIntoView();
      msgBox?.blur();
    }
  };

  const arrSharePostMenuItem: MenuItems[] = [sharePostMenuItem(onSharePostClick)];
  useEffect(() => {
    const ch: any = document.getElementsByClassName("chatHolder")[0];
    if (ch) {
      if (isEmojiOpen && !isBrowser) {
        ch.classList.remove("h-per-100");
        ch.classList.add("chatWindow-emoji-mobile-open");
      } else {
        ch.classList.remove("chatWindow-emoji-mobile-open");
        ch.classList.add("h-per-100");
      }
    }
    if (!isEmojiOpen) {
      clearReactionEmojiMessageID();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmojiOpen]);

  return (
    <Box className={`chatFooter ${disabled ? "disabled" : ""} bg-true-white ${loader ? "pos-abs" : ""}`} sx={{ bottom: loader ? (isBrowser ? "0px" : "8px") : "unset" }}>
      <Box className={`chat-text-box flex flex-direction-column ${disabled ? "disabled" : ""}`}>
        {selectedReply ? <ChatReply senderID={senderID} backgroundColorClass="bg-extra-light-gray" selectedReply={selectedReply} /> : null}
        {linkDetails && !isMessageBoxEmpty ? (
          <Box className={`chatReplyAnimation ${isTablet ? "ht-88" : "ht-64"} bg-extra-light-gray flex flex-direction-row align-items-center bor-t-r-b-l-4 overflow-h`} sx={{ margin: selectedReply ? "0px 8px 4px" : "8px 8px 4px 8px" }}>
            {linkDetails.image ? <img alt={linkDetails.title} src={`${linkDetails.image}`} className={`${isTablet ? "box-88" : "box-64"}`} style={{ objectFit: "cover" }} /> : null}
            <Box className="flex flex-direction-column pad-8">
              <Typography variant="caption" className={`${isTablet ? "ft-14" : ""} text-primary clamp-1 ft-wt-500`}>
                {linkDetails.title}
              </Typography>
              <Typography variant="caption" className={`${isTablet ? "ft-13" : "ft-11"} text-secondary clamp-1`}>
                {linkDetails.description}
              </Typography>
              <Typography variant="caption" className={`${isTablet ? "ft-12" : "ft-10"} text-primary-color clamp-1`}>
                {linkDetails.url}
              </Typography>
            </Box>
          </Box>
        ) : null}

        <Box className="chat-box">
          <IconButton onClick={disabled ? () => {} : onEmojiClick} className="box-32 mar-l-8 mar-b-8">
            {icon({ iconName: "sentiment_satisfied_alt", iconColor: `${isEmojiOpen ? "icon-primary-color" : "icon-quick-silver"}` })}
          </IconButton>
          {isMessageBoxEmpty && <span className="editable-placeholder pos-abs cur-pot-none text-secondary pad-l-48">{disabled ? "Messaging is disabled" : "Message"}</span>}
          <div ref={smRef} id="messageBox" contentEditable={!disabled} className={`msgBox  ${disabled ? "cur-pot-none text-silver-chalice" : "text-primary"}`} onKeyDown={onKeyDown} onInput={onInput} onFocus={onFocus} onBlur={onBlur} onPaste={onPaste} onClick={onClick} style={{ maxHeight: linkDetails ? "104px" : "176px" }}></div>
          <IconButton onClick={disabled ? () => {} : onSharePostIconClick} className="box-32 mar-b-8">
            {icon({ iconName: "attach_file", iconColor: `${anchorSharePost ? "icon-primary-color" : "icon-quick-silver"}` })}
          </IconButton>
        </Box>
      </Box>
      <Box className={`flex flex-direction-column ht-per-100 padding-right-gutter ${linkDetails || selectedReply ? "pad-t-12" : "pad-t-0"}`} sx={{ gap: "4px" }}>
        {selectedReply ? (
          <Box className={`w-48 ht-64 display-flex-justify-center`}>
            <IconButton className="box-48" onClick={onReplyCloseClick}>
              {icon({ iconName: "close", iconColor: "icon-secondary" })}
            </IconButton>
          </Box>
        ) : null}
        {linkDetails && !isMessageBoxEmpty ? (
          <Box className={`w-48 ${isTablet ? "ht-88" : "ht-64"} display-flex-justify-center`}>
            <IconButton className="box-48" onClick={onCloseClick}>
              {icon({ iconName: "close", iconColor: "icon-secondary" })}
            </IconButton>
          </Box>
        ) : null}
        {isMessageBoxEmpty ? (
          <Box className={`flex flex-grow align-items-end ${disabled ? "cur-pot-none" : ""}`}>
            <IconButton className="box-48 mar-b-8" onClick={disabled ? () => {} : onThumbsUpClick}>
              {icon({ iconName: "thumb_up", iconColor: disabled ? "icon-quick-silver" : "icon-safety-orange", iconCustomClass: "cur-pot-none" })}
            </IconButton>
          </Box>
        ) : (
          <Box className="flex flex-grow align-items-end">
            <IconButton disableRipple className="send display-flex-justify-center box-48 mar-b-8" sx={{ padding: 0 }} onClick={onSendClick}>
              <Box
                className="box-36 display-flex-justify-center bg-dodger-blue bor-rad-50"
                sx={{
                  "&:hover": {
                    backgroundColor: "var(--color-summer-sky) !important",
                  },
                }}
              >
                {icon({ iconName: "send", iconColor: "icon-true-white", iconCustomClass: "cur-pot-none" })}
              </Box>
            </IconButton>
          </Box>
        )}
      </Box>
      {anchorSharePost && <CreateMenu anchorElement={anchorSharePost} setAnchorElement={setAnchorSharePost} menuItemList={arrSharePostMenuItem} ref={spRef} />}
      <SharePost ref={shareDialogRef} senderID={encodeURIComponent(senderID)} onBookmarkedPostClick={onBookmarkedPostClick} onMyPostClick={onMyPostClick} ucnum={ucnum} />
      <ChatEmoji isEmojiOpen={isEmojiOpen} handleEmojiAwayClick={handleEmojiAwayClick} onEmojiCallback={onEmojiCallback} />
    </Box>
  );
};

export default forwardRef(ChatFooter);
